import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import {
  LEADERBOARD_CARD_LAYOUT,
  LEADERBOARD_RANK_COMPACT,
} from '../WorldCupLeaderboard/LeaderboardCard.constants';

export interface LeaderboardViewStyleOptions {
  compactRank?: boolean;
  windowWidth?: number;
  bottomInset?: number;
  reserveSelfBar?: boolean;
}

export const getLeaderboardViewStyles = (theme: Theme, options?: LeaderboardViewStyleOptions) => {
  const compactRank = options?.compactRank ?? false;
  const windowWidth = options?.windowWidth ?? 390;
  const bottomInset = options?.bottomInset ?? 0;
  const reserveSelfBar = options?.reserveSelfBar ?? false;
  // Clear the floating "your standing" bar (≈62px tall above the inset) so the
  // last row / footer pin is never hidden behind it.
  const listBottomPad = reserveSelfBar ? bottomInset + 90 : 80;
  const wcWebMaxWidth = Platform.OS === 'web'
    ? (windowWidth >= 1024 ? 880 : windowWidth >= 768 ? 700 : 560)
    : undefined;
  const CONTENT_MAX_WIDTH_WEB = wcWebMaxWidth != null
    ? { maxWidth: wcWebMaxWidth, width: '100%' as never, alignSelf: 'center' as const }
    : {};
  const rankHeadWidth = compactRank
    ? LEADERBOARD_RANK_COMPACT.RANK_COL_WIDTH
    : LEADERBOARD_CARD_LAYOUT.RANK_WIDTH;

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
      // Web flexbox defaults flex items to `min-height: auto`, so the FlashList
      // expands to its full content height — the scene scrolls (header rides up)
      // and virtualization is defeated. `minHeight: 0` lets the chain shrink so
      // the list becomes a bounded internal scroller, matching native (Yoga).
      ...(Platform.OS === 'web' && { minHeight: 0 as never }),
    },
    mainColumn: {
      flex: 1,
      ...CONTENT_MAX_WIDTH_WEB,
      ...(Platform.OS === 'web' && { minHeight: 0 as never }),
    },
    listFlex: {
      flex: 1,
      minHeight: 120,
    },
    listContent: {
      paddingTop: 4,
      paddingBottom: listBottomPad,
      ...CONTENT_MAX_WIDTH_WEB,
    },
    topHeader: {
      paddingHorizontal: 18,
      paddingTop: 14,
      paddingBottom: 6,
    },
    topHeaderTitleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    topHeaderAccent: {
      width: 4,
      height: 22,
      borderRadius: 2,
      backgroundColor: theme.accent,
    },
    topHeaderTitle: {
      fontSize: 24,
      fontWeight: '800',
      color: theme.gray[800],
      letterSpacing: -0.5,
    },
    topHeaderSubtitle: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[500],
      marginTop: 4,
      marginLeft: 12,
    },
    futuresHiddenBanner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 7,
      marginTop: 10,
      marginLeft: 12,
      marginRight: 18,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 10,
      backgroundColor: theme.background.inset,
    },
    futuresHiddenBannerText: {
      flex: 1,
      fontSize: 12,
      fontWeight: '600',
      color: theme.gray[600],
      lineHeight: 16,
    },
    heroRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 14,
      paddingHorizontal: 16,
      paddingTop: 14,
      paddingBottom: 12,
    },
    heroTextCol: {
      flex: 1,
      minWidth: 0,
    },
    heroTitle: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.gray[800],
      letterSpacing: -0.4,
    },
    heroSubtitle: {
      fontSize: 13,
      fontWeight: '600',
      color: theme.gray[500],
      marginTop: 4,
    },
    columnHeaderBar: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      marginHorizontal: 12,
      marginBottom: 8,
      paddingHorizontal: 10,
      paddingTop: 4,
      gap: LEADERBOARD_CARD_LAYOUT.MAIN_ROW_GAP,
    },
    columnHeaderRightCluster: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: LEADERBOARD_CARD_LAYOUT.RIGHT_CLUSTER_GAP,
      flexShrink: 0,
    },
    colHeadRank: {
      width: rankHeadWidth,
      alignItems: 'center',
    },
    colHeadUser: {
      flex: 1,
      minWidth: 0,
    },
    colHeadWinnerScorer: {
      width: LEADERBOARD_CARD_LAYOUT.WINNER_SCORER_WIDTH,
      alignItems: 'center',
    },
    colHeadStats: {
      width: LEADERBOARD_CARD_LAYOUT.STATS_WIDTH,
      alignItems: 'center',
      marginLeft: LEADERBOARD_CARD_LAYOUT.STATS_LEADING_MARGIN,
    },
    colHeadPts: {
      width: LEADERBOARD_CARD_LAYOUT.PTS_WIDTH,
      alignItems: 'flex-end',
    },
    colHeadLabel: {
      fontSize: 8,
      fontWeight: '700',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.45,
    },
    colHeadLabelLeft: {
      fontSize: 8,
      fontWeight: '700',
      color: theme.gray[500],
      textTransform: 'uppercase',
      letterSpacing: 0.45,
      textAlign: 'left',
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    emptyMonkey: {
      width: 100,
      height: 100,
      marginBottom: 16,
    },
    emptyTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    emptySubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
    },
    comingSoonContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    comingSoonBadge: {
      backgroundColor: theme.accent,
      borderRadius: 20,
      paddingHorizontal: 14,
      paddingVertical: 5,
      marginBottom: 20,
    },
    comingSoonBadgeText: {
      fontSize: 11,
      fontWeight: '800',
      color: theme.onPrimary,
      textTransform: 'uppercase',
      letterSpacing: 1.2,
    },
    comingSoonMonkey: {
      width: 160,
      height: 160,
      marginBottom: 20,
    },
    comingSoonTitle: {
      fontSize: 22,
      fontWeight: '800',
      color: theme.gray[800],
      marginBottom: 10,
      textAlign: 'center',
      letterSpacing: -0.3,
    },
    comingSoonSubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 22,
      marginBottom: 20,
    },
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    errorIconWrap: {
      marginBottom: 16,
    },
    errorTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    errorSubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
      marginBottom: 24,
    },
    errorButton: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: theme.primary,
    },
    errorButtonLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.onPrimary,
    },
  });
};
